import { useState, useCallback, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CircleHelp } from 'lucide-react'
import { NotificacionBadge } from '@/components/notificaciones'
import { GlobalSearch, GlobalSearchTrigger } from '@/core/components/GlobalSearch'
import { ClienteDetalleDialog } from '@/components/clientes/ClienteDetalleDialog'
import { ClienteFormDialog } from '@/components/clientes/ClienteFormDialog'
import { useServicios } from '@/hooks/useServicios'
import { useAltaCliente } from '@/hooks/useAltaCliente'
import type { VistaSaldoCliente } from '@/types/database'
import type { ClienteFormValues } from '@/components/clientes/wizard/clienteSchema'
import { rutaAccionCliente } from '@/lib/ruta-cliente'
import { toast } from 'sonner'

interface PageHeaderProps {
  title: string
  description?: string
}

export function PageHeader({ title, description }: PageHeaderProps) {
  const navigate = useNavigate()
  // El "?" abre la ayuda de la pantalla actual; /ayuda resuelve qué artículo le corresponde.
  const { pathname } = useLocation()
  const enAyuda = pathname === '/ayuda' || pathname.startsWith('/ayuda/')
  const [searchOpen, setSearchOpen] = useState(false)

  // Estados para dialogs de cliente
  const [clienteDetalleOpen, setClienteDetalleOpen] = useState(false)
  const [clienteFormOpen, setClienteFormOpen] = useState(false)
  const [clienteSeleccionado, setClienteSeleccionado] = useState<VistaSaldoCliente | null>(null)

  // Hooks para datos y mutaciones
  const { data: servicios = [] } = useServicios()
  const altaCliente = useAltaCliente()

  // Handler para ver detalle de cliente desde búsqueda
  const handleClienteVerDetalle = useCallback((clienteId: string) => {
    // Crear objeto mínimo - ClienteDetalleDialog cargará los datos completos internamente
    // @ts-expect-error - Minimal cliente object for dialog initialization
    setClienteSeleccionado({
      id: clienteId,
      nombre: null,
      email: null,
      telefono: null,
      cuit: null,
      estado: null,
      saldo: null,
      total_debe: null,
      total_haber: null,
      honorarios_mensuales: null,
      responsable: null,
    })
    setClienteDetalleOpen(true)
  }, [])

  // Handler para editar cliente (navega a la página)
  const handleClienteEditar = useCallback((clienteId: string) => {
    navigate(rutaAccionCliente(clienteId, 'editar'))
  }, [navigate])

  // Handler para estado de cuenta (navega a la página)
  const handleClienteEstadoCuenta = useCallback((clienteId: string) => {
    navigate(rutaAccionCliente(clienteId, 'estado-cuenta'))
  }, [navigate])

  // Handler para crear nuevo cliente
  const handleNuevoCliente = useCallback(() => {
    setClienteFormOpen(true)
  }, [])

  // Handler para submit del formulario de cliente
  // El alta abarca el cliente y sus etiquetas. Mientras dura, el wizard queda montado y deshabilitado,
  // y un submit nuevo se ignora: si falla, el usuario reintenta con lo que cargó.
  const [guardandoCliente, setGuardandoCliente] = useState(false)
  const guardandoClienteRef = useRef(false)

  const handleClienteSubmit = useCallback(async (data: ClienteFormValues) => {
    if (guardandoClienteRef.current) return
    guardandoClienteRef.current = true
    setGuardandoCliente(true)
    try {
      await altaCliente.darDeAlta(data)
      setClienteFormOpen(false)
      toast.success('Cliente creado exitosamente')
    } catch (error) {
      console.error('Error creando cliente:', error)
      toast.error('Error al crear cliente')
    } finally {
      guardandoClienteRef.current = false
      setGuardandoCliente(false)
    }
  }, [altaCliente])

  return (
    <>
      <header className="sticky top-0 z-10 flex h-14 md:h-16 shrink-0 items-center justify-between border-b bg-card px-3 md:px-6">
        <div className="min-w-0">
          <h1 className="text-lg md:text-xl font-semibold truncate">{title}</h1>
          {description && (
            <p className="hidden md:block text-sm text-muted-foreground">{description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <GlobalSearchTrigger onClick={() => setSearchOpen(true)} />
          {!enAyuda && (
            <Link
              to={`/ayuda?desde=${encodeURIComponent(pathname)}`}
              aria-label="Ayuda de esta pantalla"
              title="Ayuda de esta pantalla"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <CircleHelp className="h-5 w-5" />
            </Link>
          )}
          <NotificacionBadge />
        </div>
      </header>

      {/* Buscador Global */}
      <GlobalSearch
        isOpen={searchOpen}
        onOpenChange={setSearchOpen}
        onNuevoCliente={handleNuevoCliente}
        onClienteVerDetalle={handleClienteVerDetalle}
        onClienteEditar={handleClienteEditar}
        onClienteEstadoCuenta={handleClienteEstadoCuenta}
      />

      {/* Dialog de Detalle de Cliente */}
      <ClienteDetalleDialog
        open={clienteDetalleOpen}
        onOpenChange={setClienteDetalleOpen}
        cliente={clienteSeleccionado}
      />

      {/* Dialog de Formulario de Cliente */}
      <ClienteFormDialog
        open={clienteFormOpen}
        onOpenChange={setClienteFormOpen}
        servicios={servicios}
        onSubmit={handleClienteSubmit}
        isSaving={guardandoCliente}
      />
    </>
  )
}
